// Commented out code is required for API call, will enable once backend is ready
import { CloseCircleOutlined } from '@ant-design/icons';
import { Button, Dropdown, Form, Input, Modal, Typography } from 'antd';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import CloseIcon from '../../../assets/icons/cross.svg';
import DownIcon from '../../../assets/icons/dropdown-down-arrow.svg';
import DropdownMenu from '../../ui/DropdownMenu/DropdownMenu';
import {
  IAddAccountPayload,
  ICreateAccountPayload,
  ICreateProjectPayload,
} from '../../../libs/models/OverviewModels';
import { OverviewActions } from '../../../libs/store/actions/OverviewActions';
import { IApplicationState, useAppSelector } from '../../../libs/store/reducers';
import { storage, STORAGE_CONSTANTS } from '../../../libs/services/LocalStorage';
import { CreateAccountPopupLabels as LABELS } from './CreateAccountPopupConstants';
import './CreateAccountPopupStyles.less';


interface ICreateAccountPopupProps {
  selectedAccountName?: string;
  allowAddNewAccount?: boolean;
  onClose: () => void;
}

const CreateAccountPopup = ({
  selectedAccountName,
  onClose,
  allowAddNewAccount = true,
}: ICreateAccountPopupProps) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const userId = storage.getItem(STORAGE_CONSTANTS.userId)!;

  const { accountsProjectsMapping } = useAppSelector(
    ({ dashboard: { accountsProjectsMapping } }: IApplicationState) => ({
      accountsProjectsMapping,
    }),
  );

  const [isAccountEditable, setIsAccountEditable] = useState<boolean>(allowAddNewAccount);
  const [projectName, setProjectName] = useState<string>('');
  const [wbsCode, setWbsCode] = useState<string>('');
  const [accountOwner, setAccountOwner] = useState<string>('');
  const [ownerEmail, setOwnerEmail] = useState<string>('');
  const [useCase, setUseCase] = useState<string>('');
  const [accountName, setAccountName] = useState<string>(selectedAccountName || '');
  const [accountId, setAccountId] = useState<string>(
    selectedAccountName
      ? accountsProjectsMapping.filter((item: any) => item.accountName === selectedAccountName)[0]
          ?.accountId
      : '',
  );

  const accounts = accountsProjectsMapping.map(({ accountName }: { accountName: string }) => accountName);

  const dispatcher = {
    createAccount: (data: ICreateAccountPayload) =>
      dispatch(OverviewActions.createAccountStart(data)),
    addProject: (data: ICreateProjectPayload) => dispatch(OverviewActions.createProjectStart(data)),
    addAccount: (data: IAddAccountPayload) => dispatch(OverviewActions.addAccountStart(data)),
  };

  const createAccount = (data: ICreateAccountPayload) => dispatcher.createAccount(data);
  const addProject = (data: ICreateProjectPayload) => dispatcher.addProject(data);
  const addAccount = (data: IAddAccountPayload) => dispatcher.addAccount(data);

  const projectsDropdownMenu = (
    <DropdownMenu
      keys={accounts}
      selectedKey={accountName}
      onClick={(account: string) => {
        setAccountName(account);
        setAccountId(
          accountsProjectsMapping.filter((item: any) => item.accountName === account)[0]?.accountId,
        );
      }}
    />
  );

  const onClickSubmit = async () => {
    await form.validateFields().then(() => {
      if (isAccountEditable) {
        // if (isEmpty(projectName)) {
        addAccount({
          accountName: accountName,
          wbs_code: wbsCode,
          account_owner: accountOwner,
          owner_email: ownerEmail,
          usecase: useCase,
          userId: userId,
        });
        // } else
        //   createAccount({
        //     accountName: accountName,
        //     projectName: projectName,
        //   });
      } else {
        addProject({
          accountId: accountId,
          accountName: accountName,
          projectName: projectName,
          userId: userId,
        });
      }
    });
    onClose();
  };
  return (
    <Modal
      getContainer={false}
      width={680}
      title={allowAddNewAccount ? LABELS.title1 : LABELS.title2}
      visible
      onCancel={onClose}
      footer={null}
      closeIcon={<img src={CloseIcon} alt="Close" />}
    >
      <Form layout="vertical" name="configuration" onFinish={onClickSubmit}>
        <div className="select-account-container">
          <Typography className="select-account-label">
            {allowAddNewAccount ? LABELS.addAccountLabel : LABELS.selectAccountLabel}
          </Typography>
          {!isAccountEditable && allowAddNewAccount && (
            <Button
              type="link"
              className="edit-account-toggle"
              onClick={() => setIsAccountEditable(true)}
            >
              <u>{LABELS.addAccountButtonTitle}</u>
            </Button>
          )}
        </div>
        {isAccountEditable ? (
          <Form.Item
            name="Account"
            rules={[{ required: true, message: LABELS.accountValidationMessage1 }]}
          >
            <Input
              placeholder="-"
              //TODO : Maybe Need in Future
              // addonAfter={
              // <CloseCircleOutlined
              //   onClick={() => {
              //     setAccountName('');
              //     setIsAccountEditable(false);
              //   }}
              // />
              // }
              onChange={({ target: { value } }) => setAccountName(value)}
            />
          </Form.Item>
        ) : (
          <Form.Item
            name="Account"
            rules={[{ required: isEmpty(accountName), message: LABELS.accountValidationMessage2 }]}
          >
            <Dropdown overlay={projectsDropdownMenu} trigger={['click']}>
              <Button className="select-account-dropdown">
                {accountName || LABELS.selectAccountDropdownTitle}
                <img src={DownIcon} className="select-button-down-icon" alt="Dropdown Icon" />
              </Button>
            </Dropdown>
          </Form.Item>
        )}
        {allowAddNewAccount ? (
          <>
            <Typography className="select-project-label">{LABELS.addWbsCode}</Typography>
            <Form.Item
              name="wbsCode"
              rules={[{ required: !allowAddNewAccount, message: LABELS.wbsValidationMessage }]}
            >
              <Input placeholder="-" onChange={({ target: { value } }) => setWbsCode(value)} />
            </Form.Item>

            <Typography className="select-project-label">{LABELS.addAccountOwner}</Typography>
            <Form.Item
              name="accountOwner"
              rules={[
                { required: !allowAddNewAccount, message: LABELS.accountOwnerValidationMessage },
              ]}
            >
              <Input placeholder="-" onChange={({ target: { value } }) => setAccountOwner(value)} />
            </Form.Item>

            <Typography className="select-project-label">{LABELS.addOwnerEmail}</Typography>
            <Form.Item
              name="ownerEmail"
              rules={[
                {
                  required: !allowAddNewAccount,
                  message: LABELS.accountOwnerEmailValidationMessage,
                },
              ]}
            >
              <Input placeholder="-" onChange={({ target: { value } }) => setOwnerEmail(value)} />
            </Form.Item>

            <Typography className="select-project-label">{LABELS.addUseCase}</Typography>
            <Form.Item
              name="useCase"
              rules={[{ required: !allowAddNewAccount, message: LABELS.useCaseValidationMessage }]}
            >
              <Input placeholder="-" onChange={({ target: { value } }) => setUseCase(value)} />
            </Form.Item>
          </>
        ) : (
          <>
            <Typography className="select-project-label">{LABELS.selectProjectLabel}</Typography>
            <Form.Item
              name="project"
              rules={[{ required: !allowAddNewAccount, message: LABELS.projectValidationMessage }]}
            >
              <Input placeholder="-" onChange={({ target: { value } }) => setProjectName(value)} />
            </Form.Item>
          </>
        )}

        <Form.Item key="submit">
          <Button htmlType="submit" key="submit" className="account-submit-button" type="primary">
            {LABELS.submitButtonTitle}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateAccountPopup;
